import * as React from 'react';
import {
  Alert,
  BottomSheet,
  BottomSheetContent,
  Button,
  callStatusLabel,
  Stack,
  StatusPicker,
  toneForStatus,
  UrgentlyCompletionModal,
} from '@relay-towing/ui';
import { useCreateImpound } from '../../hooks/useCreateImpound';
import { useMember } from '../../hooks/useMember';
import { useUpdateDispatchStatus } from '../../hooks/useUpdateDispatchStatus';
import { DRIVER_CALL_STATUSES, isUnstarted } from '../../lib/callStatus';
import type { CallRecord } from './callRecord';

const CONFLICT =
  'This call was just changed by someone else. The latest is loaded; try again.';

const messageOf = (error: unknown, fallback: string) => {
  const message = (error as Error | null)?.message ?? '';
  return message.includes('Version conflict') ? CONFLICT : fallback;
};

/**
 * The call's status, at the foot of its sheet. Every status can be picked,
 * earlier ones included: when a driver could not move a call back, the
 * office had to fix every driver's status for them.
 *
 * Completing a call marked as an impound asks whether to impound the
 * vehicle, for a role that may create one. An Urgently call asks
 * Urgently's own questions instead (was it towed, was it stored), which go
 * to Urgently with the completion; it waits for Urgently's approval after.
 * The phone had no such form, so these were finished from the dashboard.
 */
export function CallStatusControl({
  call,
  onCall,
  onStart,
  starting,
  onCompleted,
  onImpounded,
}: {
  call: CallRecord;
  /** The member is one of the call's drivers. */
  onCall: boolean;
  onStart: () => void;
  starting: boolean;
  /** The call is completed. */
  onCompleted: () => void;
  /** The call is completed into an impound, with its id. */
  onImpounded: (impoundId: string) => void;
}) {
  const member = useMember();
  const updateStatus = useUpdateDispatchStatus();
  const createImpound = useCreateImpound();
  const [picking, setPicking] = React.useState(false);
  const [askImpound, setAskImpound] = React.useState(false);
  const [askUrgently, setAskUrgently] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const editsAny = member.can('calls.edit');
  if (!onCall && !editsAny) return null;

  // Accepted from an offer: the job is not the company's until the motor
  // club confirms it, which makes it DISPATCHED or scheduled, so a driver
  // has nothing to start yet. The office, with calls.edit, keeps the picker.
  if (!editsAny && call.status === 'ACCEPTED_AWAITING') {
    return (
      <Alert tone="info">
        Accepted. Waiting for the motor club to confirm the job is yours.
      </Alert>
    );
  }

  if (onCall && isUnstarted(call.status)) {
    return (
      <Button fullWidth loading={starting} onClick={onStart}>
        Start call
      </Button>
    );
  }

  const urgently = call.source === 'URGENTLY';
  const statuses = DRIVER_CALL_STATUSES.map((value) => ({
    value,
    label: callStatusLabel(value),
    tone: toneForStatus(value),
  }));

  const complete = async (
    impound: boolean,
    additionalInfo?: { towRequired: boolean; storageRequired: boolean }
  ) => {
    setError(null);
    try {
      await updateStatus.mutateAsync({
        dispatchId: call.id,
        status: 'COMPLETED',
        version: call.version,
        additionalInfo,
      });
    } catch (failure) {
      setAskImpound(false);
      setAskUrgently(false);
      setError(messageOf(failure, 'The call could not be completed.'));
      return;
    }
    setAskImpound(false);
    setAskUrgently(false);
    if (!impound) return onCompleted();
    try {
      const created = await createImpound.mutateAsync({
        dispatchId: call.id,
        storageLot: 'Main Yard',
      });
      onImpounded(created.id);
    } catch {
      // The call is completed either way; say so rather than leave it open.
      setError('The call is completed, but the impound could not be made.');
    }
  };

  const change = async (status: string) => {
    setPicking(false);
    if (status === call.status) return;
    if (status === 'COMPLETED') {
      if (urgently) return setAskUrgently(true);
      if (call.isImpound && member.can('impounds.create'))
        return setAskImpound(true);
      return complete(false);
    }
    setError(null);
    try {
      await updateStatus.mutateAsync({
        dispatchId: call.id,
        status,
        version: call.version,
      });
    } catch (failure) {
      setError(messageOf(failure, 'The status could not be changed.'));
    }
  };

  return (
    <Stack gap={2}>
      {error ? <Alert tone="destructive">{error}</Alert> : null}
      <StatusPicker
        presentation="sheet"
        fullWidth
        title="Call Status"
        statuses={statuses}
        value={call.status}
        valueLabel={callStatusLabel(call.status)}
        valueTone={toneForStatus(call.status)}
        open={picking}
        onOpenChange={setPicking}
        busy={updateStatus.isPending}
        onValueChange={change}
      />
      <UrgentlyCompletionModal
        presentation="sheet"
        isOpen={askUrgently}
        onSubmit={(towRequired, storageRequired) =>
          void complete(false, { towRequired, storageRequired })
        }
        onCancel={() => setAskUrgently(false)}
        isSubmitting={updateStatus.isPending}
      />
      <BottomSheet open={askImpound} onOpenChange={setAskImpound}>
        <BottomSheetContent
          title="Impound this vehicle?"
          description="This call is marked as an impound. Completing it can put the vehicle on the lot."
        >
          <Stack gap={2}>
            <Button
              size="xl"
              pill
              loading={updateStatus.isPending || createImpound.isPending}
              onClick={() => complete(true)}
            >
              Complete and impound
            </Button>
            <Button
              variant="secondary"
              size="xl"
              pill
              onClick={() => complete(false)}
            >
              Complete without impounding
            </Button>
          </Stack>
        </BottomSheetContent>
      </BottomSheet>
    </Stack>
  );
}
